export interface ArticleStyleVariants {
  wrapper?: string
  image?: string
  category?: string
  headline?: string
  playButton?: string
  publisher?: {
    wrapper?: string
    text?: string
    icon?: string | boolean
    ad?: string | boolean
  }
  subtitle?: string
  button?: string
}

export function getArticleStyleVariants(
  props: ArticleCardProps
): ArticleStyleVariants {
  // ─── SHARED ────────────────────────────────────────────────────────────────

  if (props.variant === 'subtle-greybg') {
    return {
      wrapper:
        'h-[304px] w-[300px] flex-col group flex bg-gray-100 transition duration-300 hover:cursor-pointer rounded-8px hover:shadow-md',
      image:
        'h-[202px] rounded-t-lg object-cover transition duration-300 group-hover:brightness-[0.8] order-1 mb-12px',
      headline:
        'text-ellipsis !font-bold text-base line-clamp-2 text-content-primary !text-[20px] order-3 px-4 !leading-[28px]',
      publisher: {
        wrapper: 'flex items-center gap-1 order-2 px-4 mb-[2px]',
        text: '!text-xs text-content-secondary font-normal !leading-[16px]',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
    }
  }

  // ─── GOOGLE ────────────────────────────────────────────────────────────────

  if (
    props.design === 'google' &&
    props.variant === 'default' &&
    props.size === 'tiny'
  ) {
    return {
      wrapper:
        'h-[88px] w-[300px] px-4 py-3 grid grid-rows-[auto_auto] grid-cols-[1fr_64px] gap-x-3 group bg-background-default hover:cursor-pointer border-article-card-border shadow-article-card rounded-12px hover:shadow-md',
      image:
        'h-[64px] w-[64px] rounded object-cover group-hover:brightness-[0.8] row-span-2 col-start-2 self-center',
      headline:
        'overflow-hidden break-words group-hover:underline !font-medium text-base line-clamp-2 text-content-primary text-sm row-start-1 col-start-1 col-span-1',
      subtitle:
        'overflow-hidden break-words text-xs text-content-secondary font-normal line-clamp-2 row-start-2 col-start-1 col-span-1',
      publisher: {
        wrapper: 'flex items-center gap-1 col-span-1 col-start-1 row-start-3',
        text: 'text-sm text-content-secondary font-normal line-clamp-1',
        icon:
          !!props.publisherIconUrl &&
          !props.ad &&
          'h-[16px] w-[16px] rounded-full',
        ad:
          !!props.publisherName &&
          !!props.ad &&
          'text-sm text-content-secondary font-normal grow text-right',
      },
    }
  }

  if (
    props.design === 'google' &&
    props.variant === 'default' &&
    props.size === 'large'
  ) {
    return {
      wrapper:
        'h-[304px] w-[612px] flex-col p-4 group flex bg-background-default hover:cursor-pointer border-article-card-border shadow-article-card rounded-12px hover:shadow-md',
      image:
        'h-[180px] w-[580px] rounded object-cover group-hover:brightness-[0.8] mb-4',
      headline:
        'h-[48px] w-[580px] text-ellipsis group-hover:underline font-semibold text-base line-clamp-2 text-content-primary text-[40px] mb-2',
      playButton: 'h-12',
      publisher: {
        wrapper: 'flex items-center gap-1',
        text: 'text-sm text-content-secondary font-normal',
        icon:
          !!props.publisherIconUrl &&
          !props.ad &&
          'h-[16px] w-[16px] rounded-full',
      },
    }
  }

  if (props.design === 'google' && props.variant === 'yolo-l') {
    return {
      wrapper:
        'h-[304px] w-[612px] p-4 group bg-background-default hover:cursor-pointer border-article-card-border shadow-article-card rounded-12px hover:shadow-md grid grid-cols-[300px,_1fr] gap-x-7',
      image:
        'h-[272px] w-[300px] rounded object-cover group-hover:brightness-[0.8] col-start-1',
      headline:
        'text-ellipsis group-hover:underline !font-bold text-base line-clamp-3 text-content-primary !text-[24px] col-start-2 !leading-8 mb-12px',
      subtitle:
        'text-sm text-content-secondary font-normal text-ellipsis line-clamp-2 mb-24px',
      publisher: {
        wrapper: 'h-[16px] flex items-center gap-1',
        text: 'text-xs text-content-secondary font-normal',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
    }
  }

  if (props.design === 'google' && props.variant === 'yolo-xl') {
    return {
      wrapper:
        'h-[304px] w-[924px] p-4 group bg-background-default hover:cursor-pointer border-article-card-border shadow-article-card rounded-12px hover:shadow-md grid grid-cols-[300px,_1fr] gap-x-7',
      image:
        'h-[272px] w-[300px] rounded object-cover group-hover:brightness-[0.8] col-start-1',
      headline:
        'text-ellipsis group-hover:underline !font-bold text-base line-clamp-2 text-content-primary !text-[24px] col-start-2 !leading-8 mb-12px',
      subtitle:
        'text-sm text-content-secondary font-normal text-ellipsis line-clamp-2 mb-24px',
      publisher: {
        wrapper: 'h-[16px] flex items-center gap-1',
        text: 'text-xs text-content-secondary font-normal',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
    }
  }

  if (props.design === 'google' && props.variant === 'yolo-xxl') {
    return {
      wrapper:
        'h-[304px] w-[1236px] p-4 group bg-background-default hover:cursor-pointer border-article-card-border shadow-article-card rounded-12px hover:shadow-md grid grid-cols-[300px,_1fr] gap-x-7',
      image:
        'h-[272px] w-[300px] rounded object-cover group-hover:brightness-[0.8] col-start-1',
      headline:
        'text-ellipsis group-hover:underline !font-bold text-base line-clamp-2 text-content-primary !text-[24px] col-start-2 !leading-8 mb-12px',
      subtitle:
        'text-sm text-content-secondary font-normal text-ellipsis line-clamp-2 mb-24px',
      publisher: {
        wrapper: 'h-[16px] flex items-center gap-1',
        text: 'text-xs text-content-secondary font-normal',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
    }
  }

  if (props.design === 'google' && props.variant === 'yolo-full') {
    return {
      wrapper:
        'h-[304px] w-[1548px] p-4 group bg-background-default hover:cursor-pointer border-article-card-border shadow-article-card rounded-12px hover:shadow-md grid grid-cols-[300px,_1fr] gap-x-7',
      image:
        'h-[272px] w-[300px] rounded object-cover group-hover:brightness-[0.8] col-start-1',
      headline:
        'text-ellipsis group-hover:underline !font-bold line-clamp-2 text-content-primary !text-[28px] col-start-2 !leading-8 mb-12px',
      subtitle:
        'text-base self-start text-content-secondary font-normal text-ellipsis line-clamp-2 mb-24px',
      publisher: {
        wrapper: 'h-[16px] flex items-center gap-1',
        text: 'text-sm text-content-secondary font-normal',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
    }
  }

  if (props.design === 'google') {
    return {
      wrapper:
        'h-[304px] w-[300px] flex-col p-4 group flex bg-background-default hover:cursor-pointer shadow-article-card rounded-12px hover:shadow-md',
      image: 'h-[180px] rounded object-cover group-hover:brightness-[0.8] mb-4',
      headline:
        'text-ellipsis group-hover:underline font-semibold text-base line-clamp-2 text-content-primary text-[40px] mb-2',
      publisher: {
        wrapper: 'flex items-center gap-1',
        text: 'text-sm text-content-secondary font-normal',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
    }
  }

  // ─── MSN ───────────────────────────────────────────────────────────────────

  if (props.design === 'msn' && props.variant === 'video-s') {
    return {
      wrapper:
        'w-[200px] h-[246px] flex-col group flex bg-background-default transition duration-300 hover:cursor-pointer border-article-card-border shadow-article-card rounded-[6px] hover:shadow-md',
      image:
        'w-[200px] h-[126px] rounded-t-lg object-cover transition duration-300 group-hover:brightness-[0.8] order-1 mb-16px',
      headline:
        'w-[200px] h-[60px] text-ellipsis !font-bold text-base line-clamp-3 text-content-primary !text-[14px] order-3 px-4 !leading-[20px]',
      playButton: 'h-5 translate-y-[-25px]',
      publisher: {
        wrapper:
          'w-[200px] h-[16px] flex items-center gap-1 order-2 px-4 mb-[8px]',
        text: '!text-xs text-content-secondary font-normal !leading-[16px]',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
    }
  }

  if (props.design === 'msn' && props.variant === 'video-l') {
    return {
      wrapper:
        'w-[437px] h-[246px] flex-col group flex bg-background-default transition duration-300 hover:cursor-pointer rounded-[6px] hover:shadow-md gap-[10px]',
      playButton: 'h-5 translate-y-[23px]',
      image:
        'w-[437px] h-[246px] rounded-lg object-cover transition duration-300 group-hover:brightness-[0.8]',
    }
  }

  if (props.design === 'msn' && props.variant === '2x2') {
    return {
      wrapper:
        'h-[620px] w-[612px] flex-col group flex bg-background-default hover:cursor-pointer border-article-card-border shadow-article-card rounded-12px hover:shadow-md',
      image:
        'h-[425px] w-[612px] rounded-t-lg object-cover group-hover:brightness-[0.8] mb-3',
      headline:
        'w-full text-ellipsis !font-bold !text-2xl line-clamp-2 text-content-primary mb-3 px-4 order-2',
      publisher: {
        wrapper: 'flex items-center gap-1 mb-3 px-4 order-1',
        text: 'text-xs text-content-secondary font-normal',
        icon:
          !!props.publisherIconUrl &&
          !props.ad &&
          'h-[16px] w-[16px] rounded-full',
      },
      subtitle:
        'text-sm text-content-secondary font-normal text-ellipsis line-clamp-2 h-[40px] max-w-[443px] order-3',
      button: 'min-w-[97px] h-[40px] rounded-lg order-4 mb-4',
    }
  }

  if (props.design === 'msn' && props.variant === 'hero') {
    return {
      wrapper:
        'h-[620px] w-[612px] flex-col group flex bg-background-default hover:cursor-pointer border-article-card-border shadow-article-card rounded-12px hover:shadow-md',
      headline:
        'h-[64px] w-full text-ellipsis !font-bold !text-2xl line-clamp-2 text-content-primary mb-4 mt-4 px-4',
      image:
        'h-[422px] w-full aspect-[3/2] object-cover group-hover:brightness-[0.8] mb-3',
      publisher: {
        wrapper: 'flex items-center gap-1 mb-1 mt-2 px-4',
        text: 'text-xs text-content-secondary font-normal',
        icon:
          !!props.publisherIconUrl &&
          !props.ad &&
          'h-[16px] w-[16px] rounded-full',
      },
      subtitle:
        'text-sm text-content-secondary font-normal text-ellipsis line-clamp-2 h-[40px] max-w-[443px] mb-3',
      button: 'min-w-[97px] h-[40px] rounded-lg',
    }
  }

  if (
    props.design === 'msn' &&
    props.variant === 'transparent' &&
    props.size === 'large'
  ) {
    return {
      wrapper:
        'h-[304px] w-[612px] group bg-background-default hover:cursor-pointer border-article-card-border shadow-article-card rounded-8px hover:shadow-md',
      image:
        'h-[304px] w-[612px] rounded-lg object-cover group-hover:brightness-[0.8]',
      headline:
        'h-[56px] text-ellipsis !font-bold text-base line-clamp-2 text-white !text-[20px] px-4 !leading-[28px] relative bottom-[104px]',
      playButton: 'h-32',
      publisher: {
        wrapper: 'flex items-center gap-1 px-4 relative bottom-[184px]',
        text: '!text-xs text-white font-normal !leading-[16px]',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
        ad:
          !!props.ad &&
          '!text-xs text-white font-normal !leading-[16px] bottom-[100px] px-4 relative',
      },
    }
  }

  if (
    props.design === 'msn' &&
    props.variant === 'transparent' &&
    props.size === 'default'
  ) {
    return {
      wrapper:
        'h-[304px] w-[300px] group bg-background-default hover:cursor-pointer border-article-card-border shadow-article-card rounded-8px hover:shadow-md',
      image:
        'h-[304px] w-[300px] rounded-lg object-cover group-hover:brightness-[0.8]',
      headline:
        'text-ellipsis  !font-bold text-base line-clamp-3 text-content-inverted-primary !text-[20px] px-4 !leading-[28px] relative bottom-[116px]',
      publisher: {
        wrapper: 'flex items-center gap-1 px-4 relative bottom-[216px]',
        text: '!text-xs text-content-inverted-secondary font-normal !leading-[16px]',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
    }
  }

  if (props.design === 'msn' && props.variant === 'title-msn') {
    return {
      wrapper:
        'h-[304px] w-[300px] flex-col group flex bg-background-default transition duration-300 hover:cursor-pointer border-article-card-border shadow-article-card rounded-8px hover:shadow-md',
      image:
        'h-[158px] rounded-t-lg object-cover transition duration-300 group-hover:brightness-[0.8] order-1 mb-12px',
      headline:
        'h-[84px] text-ellipsis !font-bold text-base line-clamp-3 text-content-primary !text-[20px] order-3 px-4 !leading-[28px]',
      publisher: {
        wrapper: 'flex items-center gap-1 order-2 px-4',
        text: '!text-xs text-content-secondary font-normal !leading-[16px]',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
    }
  }

  if (props.design === 'msn' && props.size === 'tiny') {
    return {
      wrapper:
        'h-[76px] w-[284px] bg-background-default hover:cursor-pointer flex flex-col gap-4px rounded-6px hover:bg-action-neutral-onSubtle-hover p-8px',
      headline:
        'text-ellipsis !font-semibold text-base line-clamp-2 text-content-primary !text-sm !leading-[20px] order-2',
      publisher: {
        wrapper: 'h-[16px] flex items-center gap-1 order-1',
        text: 'text-xs text-content-secondary font-normal',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
    }
  }

  if (props.design === 'msn' && props.variant === 'yolo-l') {
    return {
      wrapper:
        'h-[304px] w-[612px] group bg-background-default hover:cursor-pointer border-article-card-border shadow-article-card rounded-12px hover:shadow-md grid grid-cols-[300px,_1fr] gap-x-7 pr-[24px]',
      image:
        'h-[304px] w-[300px] rounded-l-lg object-cover group-hover:brightness-[0.8] col-start-1',
      headline:
        'text-ellipsis !font-bold text-base line-clamp-3 text-content-primary !text-[24px] col-start-2 !leading-8 mb-12px order-2',
      subtitle:
        'text-sm text-content-secondary font-normal text-ellipsis line-clamp-2 mb-24px order-3',
      publisher: {
        wrapper: 'h-[16px] flex items-center gap-1 order-1 mb-3',
        text: 'text-xs text-content-secondary font-normal',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
      button: 'w-fit !self-end order-4',
    }
  }

  if (props.design === 'msn' && props.variant === 'yolo-xl') {
    return {
      wrapper:
        'h-[304px] w-[924px] group bg-background-default hover:cursor-pointer border-article-card-border shadow-article-card rounded-12px hover:shadow-md grid grid-cols-[300px,_1fr] gap-x-7 pr-[24px]',
      image:
        'h-[304px] w-[300px] rounded-l-lg object-cover group-hover:brightness-[0.8] col-start-1',
      headline:
        'text-ellipsis !font-bold text-base line-clamp-3 text-content-primary !text-[24px] col-start-2 !leading-8 mb-12px order-2',
      subtitle:
        'text-sm text-content-secondary font-normal text-ellipsis line-clamp-2 mb-24px order-3',
      publisher: {
        wrapper: 'h-[16px] flex items-center gap-1 order-1 mb-3',
        text: 'text-xs text-content-secondary font-normal',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
      button: 'w-fit !self-end order-4',
    }
  }

  if (props.design === 'msn' && props.variant === 'yolo-xxl') {
    return {
      wrapper:
        'h-[304px] w-[1236px] group bg-background-default hover:cursor-pointer border-article-card-border shadow-article-card rounded-12px hover:shadow-md grid grid-cols-[612px,_1fr] gap-x-7 pr-[24px]',
      image:
        'h-[304px] w-[612px] rounded-l-lg object-cover group-hover:brightness-[0.8] col-start-1',
      headline:
        'text-ellipsis !font-bold text-base line-clamp-3 text-content-primary !text-[24px] col-start-2 !leading-8 mb-12px order-2',
      subtitle:
        'text-sm text-content-secondary font-normal text-ellipsis line-clamp-2 mb-24px order-3',
      publisher: {
        wrapper: 'h-[16px] flex items-center gap-1 order-1 mb-3',
        text: 'text-xs text-content-secondary font-normal',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
      button: 'w-fit !self-end order-4',
    }
  }

  if (props.design === 'msn' && props.variant === 'yolo-full') {
    return {
      wrapper:
        'h-[304px] w-[1548px] group bg-background-default hover:cursor-pointer border-article-card-border shadow-article-card rounded-12px hover:shadow-md grid grid-cols-[612px,_1fr] gap-x-7 pr-[24px]',
      image:
        'h-[304px] w-[798px] rounded-l-lg object-cover group-hover:brightness-[0.8] col-start-1',
      headline:
        'text-ellipsis !font-bold line-clamp-3 text-content-primary !text-[28px] col-start-2 !leading-8 mb-12px order-2',
      subtitle:
        'text-base text-content-secondary font-normal text-ellipsis line-clamp-2 mb-24px order-3',
      publisher: {
        wrapper: 'h-[16px] flex items-center gap-1 order-1 mb-3',
        text: 'text-sm text-content-secondary font-normal',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
      button: 'w-fit !self-end order-4',
    }
  }

  if (props.design === 'msn') {
    // The Inventory content uses object-contain to avoid cropping product images
    if (props.publisherName === 'The Inventory') {
      return {
        wrapper:
          'h-[304px] w-[300px] flex-col group flex bg-background-default transition duration-300 hover:cursor-pointer border-article-card-border shadow-article-card rounded-8px hover:shadow-md',
        image:
          'h-[202px] rounded-t-lg object-contain transition duration-300 group-hover:brightness-[0.8] order-1 mb-12px',
        headline:
          'text-ellipsis !font-bold text-base line-clamp-2 text-content-primary !text-[20px] order-3 px-4 !leading-[28px]',
        publisher: {
          wrapper: 'flex items-center gap-1 order-2 px-4 mb-[2px]',
          text: '!text-xs text-content-secondary font-normal !leading-[16px]',
          icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
        },
      }
    }
    return {
      wrapper:
        'h-[304px] w-[300px] flex-col group flex bg-background-default transition duration-300 hover:cursor-pointer border-article-card-border shadow-article-card rounded-8px hover:shadow-md',
      image:
        'h-[202px] rounded-t-lg object-cover transition duration-300 group-hover:brightness-[0.8] order-1 mb-12px',
      headline:
        'text-ellipsis !font-bold text-base line-clamp-2 text-content-primary !text-[20px] order-3 px-4 !leading-[28px]',
      publisher: {
        wrapper: 'flex items-center gap-1 order-2 px-4 mb-[2px]',
        text: '!text-xs text-content-secondary font-normal !leading-[16px]',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
    }
  }

  // ─── QUARTZ ────────────────────────────────────────────────────────────────

  if (props.design === 'quartz' && props.size === 'tiny') {
    return {
      wrapper:
        'w-[284px] bg-background-default dark:bg-background-neutral-subtle hover:cursor-pointer flex flex-col gap-4px rounded-6px hover:bg-action-neutral-onSubtle-hover p-8px transition duration-200',
      category: 'flex items-center justify-between order-1',
      headline:
        'font-epilogue text-ellipsis !font-semibold text-base line-clamp-2 text-content-primary !text-sm !leading-[20px] order-2',
      publisher: {
        wrapper: 'h-[16px] flex items-center gap-1 order-1',
        text: 'text-xs text-content-secondary font-normal',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
    }
  }

  if (props.design === 'quartz' && props.size === 'large') {
    return {
      wrapper:
        'h-[304px] w-[612px] group bg-background-default dark:bg-background-neutral-subtle hover:cursor-pointer rounded-12px overflow-hidden hover:shadow-[0_8px_24px_rgba(0,0,0,0.1)] hover:-translate-y-1 transition duration-200',
      image:
        'h-[304px] w-[612px] rounded-lg object-cover group-hover:brightness-[0.8]',
      headline:
        'font-epilogue text-ellipsis !font-bold text-base line-clamp-2 text-white !text-[20px] px-4 !leading-[28px] relative bottom-[104px]',
      publisher: {
        wrapper: 'flex items-center gap-1 px-4 relative bottom-[184px]',
        text: '!text-xs text-white font-normal !leading-[16px]',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
    }
  }

  if (props.design === 'quartz' && props.variant === 'yolo-l') {
    return {
      wrapper:
        'h-[304px] w-[612px] group bg-background-default dark:bg-background-neutral-subtle hover:cursor-pointer rounded-12px hover:shadow-[0_8px_24px_rgba(0,0,0,0.1)] hover:-translate-y-1 overflow-hidden grid grid-cols-[300px,_1fr] pr-[24px] transition duration-200',
      image:
        'h-[304px] w-[300px] object-cover group-hover:brightness-[0.8] col-start-1',
      headline:
        'font-epilogue text-ellipsis !font-bold text-base line-clamp-3 text-content-primary !text-[20px] col-start-2 !leading-[26px] mb-3 order-2',
      publisher: {
        wrapper: 'h-[16px] flex items-center gap-1 order-1 mb-3 pt-6 px-1',
        text: '!text-[13px] text-content-secondary font-normal',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
      button: 'w-fit !self-end order-4',
    }
  }

  if (props.design === 'quartz' && props.variant === 'yolo-xl') {
    return {
      wrapper:
        'h-[304px] w-[924px] group bg-background-default dark:bg-background-neutral-subtle hover:cursor-pointer rounded-12px hover:shadow-[0_8px_24px_rgba(0,0,0,0.1)] hover:-translate-y-1 overflow-hidden grid grid-cols-[300px,_1fr] pr-[24px] transition duration-200',
      image:
        'h-[304px] w-[300px] object-cover group-hover:brightness-[0.8] col-start-1',
      headline:
        'font-epilogue text-ellipsis !font-bold text-base line-clamp-3 text-content-primary !text-[20px] col-start-2 !leading-[26px] mb-3 order-2',
      publisher: {
        wrapper: 'h-[16px] flex items-center gap-1 order-1 mb-3 pt-6 px-1',
        text: '!text-[13px] text-content-secondary font-normal',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
      button: 'w-fit !self-end order-4',
    }
  }

  if (props.design === 'quartz' && props.variant === 'yolo-xxl') {
    return {
      wrapper:
        'h-[304px] w-[1236px] group bg-background-default dark:bg-background-neutral-subtle hover:cursor-pointer rounded-12px hover:shadow-[0_8px_24px_rgba(0,0,0,0.1)] hover:-translate-y-1 overflow-hidden grid grid-cols-[612px,_1fr] pr-[24px] transition duration-200',
      image:
        'h-[304px] w-[612px] object-cover group-hover:brightness-[0.8] col-start-1',
      headline:
        'font-epilogue text-ellipsis !font-bold text-base line-clamp-3 text-content-primary !text-[24px] col-start-2 !leading-8 mb-3 order-2',
      publisher: {
        wrapper: 'h-[16px] flex items-center gap-1 order-1 mb-3 pt-6 px-1',
        text: '!text-[13px] text-content-secondary font-normal',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
      button: 'w-fit !self-end order-4',
    }
  }

  if (props.design === 'quartz' && props.variant === 'yolo-full') {
    return {
      wrapper:
        'h-[304px] w-[1548px] group bg-background-default dark:bg-background-neutral-subtle hover:cursor-pointer rounded-12px hover:shadow-[0_8px_24px_rgba(0,0,0,0.1)] hover:-translate-y-1 overflow-hidden grid grid-cols-[612px,_1fr] pr-[24px] transition duration-200',
      image:
        'h-[304px] w-[798px] object-cover group-hover:brightness-[0.8] col-start-1',
      headline:
        'font-epilogue text-ellipsis !font-bold line-clamp-3 text-content-primary !text-[28px] col-start-2 !leading-8 mb-3 order-2',
      publisher: {
        wrapper: 'h-[16px] flex items-center gap-1 order-1 mb-3 pt-6 px-1',
        text: 'text-sm text-content-secondary font-normal',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
      button: 'w-fit !self-end order-4',
    }
  }

  if (props.design === 'quartz') {
    return {
      wrapper:
        'h-[304px] w-[300px] flex-col group flex overflow-hidden bg-background-default dark:bg-background-neutral-subtle transition duration-200 hover:cursor-pointer rounded-8px hover:shadow-[0_8px_24px_rgba(0,0,0,0.1)] hover:-translate-y-1',
      image:
        'w-full h-[202px] object-cover shrink-0 transition duration-300 group-hover:brightness-[0.8] order-1',
      category: 'flex items-center justify-between order-2 px-4 pt-3 shrink-0',
      headline:
        'font-epilogue text-ellipsis !font-bold line-clamp-2 text-content-primary !text-[20px] order-3 px-4 pt-1 mb-2 !leading-[26px] shrink-0',
      publisher: {
        wrapper: 'flex items-center gap-1 order-4 px-4 pb-3 mt-auto',
        text: '!text-[13px] text-content-secondary font-normal',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
    }
  }

  return {}
}
